import {
	applyPan,
	applyPinchZoom,
	applyWheelZoom,
	clampImageScale,
	IMAGE_ZOOM_MAX,
	IMAGE_ZOOM_MIN,
	IMAGE_ZOOM_STEP,
	resetImageZoomTransform,
	type ImageZoomTransform,
} from '@/lib/image-zoom-gestures';
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

const SWIPE_THRESHOLD_PX = 50;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_DISTANCE_PX = 24;

type TouchSession = {
	mode: 'idle' | 'pan' | 'pinch' | 'swipe-candidate';
	startX: number;
	startY: number;
	lastX: number;
	lastY: number;
	startDistance: number;
	startScale: number;
	startTime: number;
};

type LastTap = {
	time: number;
	x: number;
	y: number;
};

function touchDistance(touches: TouchList): number {
	if (touches.length < 2) return 0;
	const dx = touches[0].clientX - touches[1].clientX;
	const dy = touches[0].clientY - touches[1].clientY;
	return Math.hypot(dx, dy);
}

export interface UseImageZoomGesturesOptions {
	enabled: boolean;
	onSwipeLeft?: () => void;
	onSwipeRight?: () => void;
}

export function useImageZoomGestures(
	viewportRef: RefObject<HTMLElement | null>,
	{ enabled, onSwipeLeft, onSwipeRight }: UseImageZoomGesturesOptions,
) {
	const [transform, setTransform] = useState<ImageZoomTransform>(() => resetImageZoomTransform());
	const transformRef = useRef(transform);
	const touchSessionRef = useRef<TouchSession | null>(null);
	const lastTapRef = useRef<LastTap | null>(null);

	useEffect(() => {
		transformRef.current = transform;
	}, [transform]);

	const reset = useCallback(() => {
		setTransform(resetImageZoomTransform());
	}, []);

	const zoomIn = useCallback(() => {
		setTransform(prev => {
			const nextScale = clampImageScale(prev.scale + IMAGE_ZOOM_STEP * 2);
			if (nextScale === IMAGE_ZOOM_MIN) {
				return resetImageZoomTransform();
			}
			return { ...prev, scale: nextScale };
		});
	}, []);

	const zoomOut = useCallback(() => {
		setTransform(prev => {
			const nextScale = clampImageScale(prev.scale - IMAGE_ZOOM_STEP * 2);
			if (nextScale === IMAGE_ZOOM_MIN) {
				return resetImageZoomTransform();
			}
			return { ...prev, scale: nextScale };
		});
	}, []);

	const toggleDoubleTapZoom = useCallback(() => {
		setTransform(prev => {
			if (prev.scale > IMAGE_ZOOM_MIN) {
				return resetImageZoomTransform();
			}
			return { scale: 2, translateX: 0, translateY: 0 };
		});
	}, []);

	useEffect(() => {
		const viewport = viewportRef.current;
		if (!viewport || !enabled) return;

		function onWheel(event: WheelEvent) {
			event.preventDefault();
			const rect = viewport!.getBoundingClientRect();
			setTransform(prev =>
				applyWheelZoom(
					prev,
					event.deltaY,
					event.clientX - rect.left,
					event.clientY - rect.top,
					rect.width,
					rect.height,
				),
			);
		}

		function onTouchStart(event: TouchEvent) {
			const now = Date.now();
			if (event.touches.length === 1) {
				const touch = event.touches[0];
				const lastTap = lastTapRef.current;
				if (lastTap) {
					const deltaX = touch.clientX - lastTap.x;
					const deltaY = touch.clientY - lastTap.y;
					const distance = Math.hypot(deltaX, deltaY);
					if (now - lastTap.time <= DOUBLE_TAP_MS && distance <= DOUBLE_TAP_DISTANCE_PX) {
						toggleDoubleTapZoom();
						lastTapRef.current = null;
						touchSessionRef.current = null;
						return;
					}
				}
				touchSessionRef.current = {
					mode: transformRef.current.scale > IMAGE_ZOOM_MIN ? 'pan' : 'swipe-candidate',
					startX: touch.clientX,
					startY: touch.clientY,
					lastX: touch.clientX,
					lastY: touch.clientY,
					startDistance: 0,
					startScale: transformRef.current.scale,
					startTime: now,
				};
				return;
			}

			if (event.touches.length === 2) {
				lastTapRef.current = null;
				touchSessionRef.current = {
					mode: 'pinch',
					startX: 0,
					startY: 0,
					lastX: 0,
					lastY: 0,
					startDistance: touchDistance(event.touches),
					startScale: transformRef.current.scale,
					startTime: now,
				};
			}
		}

		function onTouchMove(event: TouchEvent) {
			const session = touchSessionRef.current;
			if (!session) return;

			if (session.mode === 'pinch' && event.touches.length >= 2) {
				event.preventDefault();
				const distance = touchDistance(event.touches);
				if (session.startDistance <= 0) return;
				const scaleFactor = distance / session.startDistance;
				setTransform(prev => applyPinchZoom({ ...prev, scale: session.startScale }, scaleFactor));
				return;
			}

			if (session.mode === 'pan' && event.touches.length === 1) {
				event.preventDefault();
				const touch = event.touches[0];
				const deltaX = touch.clientX - session.lastX;
				const deltaY = touch.clientY - session.lastY;
				session.lastX = touch.clientX;
				session.lastY = touch.clientY;
				setTransform(prev => applyPan(prev, deltaX, deltaY));
				return;
			}

			if (session.mode === 'swipe-candidate' && event.touches.length === 1) {
				const touch = event.touches[0];
				const deltaX = Math.abs(touch.clientX - session.startX);
				const deltaY = Math.abs(touch.clientY - session.startY);
				if (deltaX > 12 && deltaX > deltaY) {
					session.mode = 'swipe-candidate';
				}
			}
		}

		function onTouchEnd(event: TouchEvent) {
			const session = touchSessionRef.current;
			if (!session) return;

			if (session.mode === 'swipe-candidate' && event.changedTouches.length === 1) {
				const touch = event.changedTouches[0];
				const deltaX = touch.clientX - session.startX;
				const deltaY = touch.clientY - session.startY;
				if (Math.abs(deltaX) >= SWIPE_THRESHOLD_PX && Math.abs(deltaX) > Math.abs(deltaY)) {
					lastTapRef.current = null;
					if (deltaX < 0) {
						onSwipeLeft?.();
					} else {
						onSwipeRight?.();
					}
				} else if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX && Math.abs(deltaY) < SWIPE_THRESHOLD_PX) {
					lastTapRef.current = {
						time: Date.now(),
						x: touch.clientX,
						y: touch.clientY,
					};
				}
			}

			if (event.touches.length === 0) {
				touchSessionRef.current = null;
			}
		}

		let isMousePanning = false;
		let mouseLastX = 0;
		let mouseLastY = 0;

		function onMouseDown(event: MouseEvent) {
			if (event.button !== 0 || transformRef.current.scale <= IMAGE_ZOOM_MIN) return;
			isMousePanning = true;
			mouseLastX = event.clientX;
			mouseLastY = event.clientY;
			event.preventDefault();
		}

		function onMouseMove(event: MouseEvent) {
			if (!isMousePanning) return;
			const deltaX = event.clientX - mouseLastX;
			const deltaY = event.clientY - mouseLastY;
			mouseLastX = event.clientX;
			mouseLastY = event.clientY;
			setTransform(prev => applyPan(prev, deltaX, deltaY));
		}

		function onMouseUp() {
			isMousePanning = false;
		}

		viewport.addEventListener('wheel', onWheel, { passive: false });
		viewport.addEventListener('touchstart', onTouchStart, { passive: false });
		viewport.addEventListener('touchmove', onTouchMove, { passive: false });
		viewport.addEventListener('touchend', onTouchEnd);
		viewport.addEventListener('touchcancel', onTouchEnd);
		viewport.addEventListener('mousedown', onMouseDown);
		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseUp);

		return () => {
			viewport.removeEventListener('wheel', onWheel);
			viewport.removeEventListener('touchstart', onTouchStart);
			viewport.removeEventListener('touchmove', onTouchMove);
			viewport.removeEventListener('touchend', onTouchEnd);
			viewport.removeEventListener('touchcancel', onTouchEnd);
			viewport.removeEventListener('mousedown', onMouseDown);
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);
		};
	}, [enabled, onSwipeLeft, onSwipeRight, toggleDoubleTapZoom, viewportRef]);

	return {
		transform,
		reset,
		zoomIn,
		zoomOut,
		canZoomOut: transform.scale > IMAGE_ZOOM_MIN,
		canZoomIn: transform.scale < IMAGE_ZOOM_MAX,
	};
}
